import React, { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { decode } from 'he';
import { useForm } from 'react-hook-form';

import { Modal } from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { createEmailTemplate, updateEmailTemplate } from '../../../modules/comms/api/comms.gateway';
import { EmailTemplate } from '../../../modules/comms/types';
import { showErrorToast, showSuccessToast } from '../../../utils/errorHandler';

import './EmailTemplateModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: Partial<EmailTemplate> | null;
}

type EmailTemplateFormValues = {
  template_key: string;
  name: string;
  subject_template: string;
  body_template: string;
  placeholders: string[];
  default_variables: Record<string, unknown>;
  signature: string;
  is_active: boolean;
};

const EMAIL_TEMPLATE_FORM_DEFAULTS: EmailTemplateFormValues = {
  template_key: '',
  name: '',
  subject_template: '',
  body_template: '',
  placeholders: [],
  default_variables: {},
  signature: '',
  is_active: true,
};

const PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
const PLACEHOLDER_CLASS = 'email-placeholder-chip';

function sanitizeHtml(html?: string): string {
  const decoded = decode(html ?? '');
  return DOMPurify.sanitize(decoded, { USE_PROFILES: { html: true } });
}

function createPlaceholderChip(doc: Document, key: string): HTMLSpanElement {
  const span = doc.createElement('span');
  span.className = PLACEHOLDER_CLASS;
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-placeholder-key', key);
  span.textContent = `{{ ${key} }}`;
  return span;
}

function replacePlaceholdersInTextNode(textNode: Text, doc: Document) {
  const text = textNode.nodeValue ?? '';
  PLACEHOLDER_REGEX.lastIndex = 0;

  if (!PLACEHOLDER_REGEX.test(text)) return;

  PLACEHOLDER_REGEX.lastIndex = 0;
  const fragment = doc.createDocumentFragment();

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
    const [fullMatch, key] = match;
    const start = match.index;

    if (start > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)));
    }

    fragment.appendChild(createPlaceholderChip(doc, key));
    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
}

function htmlToEditorHtml(html?: string): string {
  const safeHtml = sanitizeHtml(html);

  const doc = document.implementation.createHTMLDocument('');
  const wrapper = doc.createElement('div');
  wrapper.innerHTML = safeHtml;

  const walker = doc.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  textNodes.forEach((node) => replacePlaceholdersInTextNode(node, doc));

  return wrapper.innerHTML;
}

function textToEditorHtml(text?: string): string {
  const decoded = decode(text ?? '');
  const escaped = decoded
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    PLACEHOLDER_REGEX,
    (_match, key) =>
      `<span class="${PLACEHOLDER_CLASS}" contenteditable="false" data-placeholder-key="${key}">{{ ${key} }}</span>`
  );
}

function restorePlaceholdersFromContainer(container: HTMLElement): string {
  const clone = container.cloneNode(true) as HTMLElement;
  const chips = clone.querySelectorAll(`.${PLACEHOLDER_CLASS}`);

  chips.forEach((chip) => {
    const key = chip.getAttribute('data-placeholder-key') || '';
    chip.replaceWith(document.createTextNode(`{{ ${key} }}`));
  });

  return clone.innerHTML;
}

function restorePlaceholdersFromTextContainer(container: HTMLElement): string {
  const clone = container.cloneNode(true) as HTMLElement;
  const chips = clone.querySelectorAll(`.${PLACEHOLDER_CLASS}`);

  chips.forEach((chip) => {
    const key = chip.getAttribute('data-placeholder-key') || '';
    chip.replaceWith(document.createTextNode(`{{ ${key} }}`));
  });

  return clone.textContent ?? '';
}

function isInsidePlaceholder(node: Node | null): HTMLElement | null {
  let current: Node | null = node;

  while (current) {
    if (
      current instanceof HTMLElement &&
      current.classList.contains(PLACEHOLDER_CLASS)
    ) {
      return current;
    }
    current = current.parentNode;
  }

  return null;
}

function selectionTouchesPlaceholder(root: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  const { startContainer, endContainer, collapsed } = range;

  if (isInsidePlaceholder(startContainer) || isInsidePlaceholder(endContainer)) {
    return true;
  }

  const chips = root.querySelectorAll(`.${PLACEHOLDER_CLASS}`);
  for (const chip of chips) {
    if (range.intersectsNode(chip)) return true;
  }

  if (!collapsed) return false;

  const startPlaceholder = getAdjacentPlaceholder(startContainer, range.startOffset, 'backward');
  const endPlaceholder = getAdjacentPlaceholder(startContainer, range.startOffset, 'forward');

  return Boolean(startPlaceholder || endPlaceholder);
}

function getAdjacentPlaceholder(
  container: Node,
  offset: number,
  direction: 'backward' | 'forward'
): HTMLElement | null {
  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent ?? '';
    if (direction === 'backward' && offset > 0) return null;
    if (direction === 'forward' && offset < text.length) return null;

    const sibling =
      direction === 'backward' ? container.previousSibling : container.nextSibling;

    return isInsidePlaceholder(sibling) || (sibling instanceof HTMLElement && sibling.classList.contains(PLACEHOLDER_CLASS) ? sibling : null);
  }

  if (container instanceof HTMLElement) {
    const targetIndex = direction === 'backward' ? offset - 1 : offset;
    const sibling = container.childNodes[targetIndex] ?? null;

    return isInsidePlaceholder(sibling) || (sibling instanceof HTMLElement && sibling.classList.contains(PLACEHOLDER_CLASS) ? sibling : null);
  }

  return null;
}

type LockedTokenTextEditorProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

const LockedTokenTextEditor: React.FC<LockedTokenTextEditorProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Enter text...',
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorHtml = useMemo(() => textToEditorHtml(value), [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    if (el.innerHTML !== editorHtml) {
      el.innerHTML = editorHtml || '';
    }
  }, [editorHtml]);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    onChange(restorePlaceholdersFromTextContainer(el));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = editorRef.current;
    if (!el) return;

    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (selectionTouchesPlaceholder(el)) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          className="email-subject-editor min-h-[44px] px-4 py-3 outline-none text-sm whitespace-pre-wrap break-words"
          data-placeholder={placeholder}
        />
      </div>
    </div>
  );
};

type EditableHtmlBoxProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
  placeholder?: string;
};

const EditableHtmlBox: React.FC<EditableHtmlBoxProps> = ({
  label,
  value,
  onChange,
  minHeight = 220,
  placeholder = 'Start typing...',
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorHtml = useMemo(() => htmlToEditorHtml(value), [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    if (el.innerHTML !== editorHtml) {
      el.innerHTML = editorHtml || '';
    }
  }, [editorHtml]);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    onChange(restorePlaceholdersFromContainer(el));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = editorRef.current;
    if (!el) return;

    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (selectionTouchesPlaceholder(el)) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 text-xs text-gray-500 dark:text-gray-400">
          Visual editor
        </div>

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          className="email-template-editor p-4 outline-none text-sm max-w-none"
          style={{ minHeight }}
          data-placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export const EmailTemplateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSaved,
  initial,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState,
  } = useForm<EmailTemplateFormValues>({
    defaultValues: EMAIL_TEMPLATE_FORM_DEFAULTS,
  });

  const subjectTemplate = watch('subject_template');
  const bodyTemplate = watch('body_template');
  const signatureTemplate = watch('signature');

  useEffect(() => {
    if (initial) {
      reset({
        template_key: initial.template_key ?? '',
        name: initial.name ?? '',
        subject_template: initial.subject_template ?? '',
        body_template: initial.body_template ?? '',
        placeholders: initial.placeholders ?? [],
        default_variables: initial.default_variables ?? {},
        signature: initial.signature ?? '',
        is_active: initial.is_active ?? true,
      });
    } else {
      reset({ ...EMAIL_TEMPLATE_FORM_DEFAULTS });
    }
  }, [initial, reset]);

  const onSubmit = async (data: EmailTemplateFormValues) => {
    try {
      if (initial && (initial as any)._id) {
        await updateEmailTemplate((initial as any)._id, data);
        showSuccessToast('Email template updated');
      } else {
        await createEmailTemplate(data);
        showSuccessToast('Email template created');
      }

      onSaved();
      onClose();
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initial ? 'Edit Email Template' : 'Create Email Template'}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="email-template-modal space-y-4">
        <Input
          label="Template key"
          {...register('template_key', { required: 'Template key required' })}
          disabled
          error={
            formState.errors.template_key
              ? String((formState.errors.template_key as any).message)
              : undefined
          }
        />

        <Input
          label="Name"
          {...register('name', { required: 'Name required' })}
          disabled
          error={
            formState.errors.name
              ? String((formState.errors.name as any).message)
              : undefined
          }
        />

        <LockedTokenTextEditor
          label="Subject template"
          value={subjectTemplate ?? ''}
          onChange={(nextValue) =>
            setValue('subject_template', nextValue, { shouldDirty: true })
          }
          placeholder="Write the subject here..."
        />

        <EditableHtmlBox
          label="Body template"
          value={bodyTemplate ?? ''}
          onChange={(nextHtml) =>
            setValue('body_template', nextHtml, { shouldDirty: true })
          }
          minHeight={260}
          placeholder="Write the email body here..."
        />

        <EditableHtmlBox
          label="Signature"
          value={signatureTemplate ?? ''}
          onChange={(nextHtml) =>
            setValue('signature', nextHtml, { shouldDirty: true })
          }
          minHeight={120}
          placeholder="Write the signature here..."
        />

        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            View raw HTML
          </summary>

          <div className="p-4 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Body template (HTML source)
              </label>
              <textarea
                {...register('body_template')}
                rows={8}
                spellCheck={false}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono text-app-text dark:text-gray-100 outline-none"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Signature (HTML source)
              </label>
              <textarea
                {...register('signature')}
                rows={4}
                spellCheck={false}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono text-app-text dark:text-gray-100 outline-none"
              />
            </div>
          </div>
        </details>

        <div className="flex gap-2 justify-end pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Modal>
  );
};


